// @ts-nocheck
/* eslint-disable react-refresh/only-export-components */
import { useEffect, useRef, useState } from 'react'
import { C, G } from '../../lib/colors'
import { normalizeLanguageCode } from '../../lib/i18n'
import { landingPlanPerks, planCadenceForLang } from '../../lib/subscription-plans'
import { priceForCard } from '../../lib/pricing'
import { buildLandingStats } from '../../lib/landing-stats'
import { buildLandingGenreGroups } from '../../lib/landing-genre-groups'

// Plan names are brand names — same across every language.
const BRAND_PLAN_NAMES = ["Starter", "Author", "Pro"];
const PLAN_NAMES = {
  EN: BRAND_PLAN_NAMES, DA: BRAND_PLAN_NAMES, DE: BRAND_PLAN_NAMES, FR: BRAND_PLAN_NAMES,
  IT: BRAND_PLAN_NAMES, ES: BRAND_PLAN_NAMES, PT: BRAND_PLAN_NAMES, NL: BRAND_PLAN_NAMES,
  SE: BRAND_PLAN_NAMES, PL: BRAND_PLAN_NAMES,
};

const PLAN_DESCRIPTIONS = {
  EN: [
    "For first-time authors ready to publish.",
    "For authors with a steady publishing rhythm.",
    "For serious self-publishers with high output.",
  ],
  DA: [
    "Til dig der vil udgive din første bog.",
    "Til forfattere med fast udgivelsestempo.",
    "Til seriøse self-publishers og højt udgivelsestempo.",
  ],
  DE: [
    "Für deine erste Buchveröffentlichung.",
    "Für Autoren mit regelmäßigem Veröffentlichungsrhythmus.",
    "Für ernsthafte Self-Publisher mit hohem Output.",
  ],
  FR: [
    "Pour votre premier livre publié.",
    "Pour les auteurs avec un rythme de publication régulier.",
    "Pour les auto-éditeurs sérieux avec un volume élevé.",
  ],
  IT: [
    "Per il tuo primo libro pubblicato.",
    "Per autori con un ritmo di pubblicazione costante.",
    "Per self-publisher seri con alta produttività.",
  ],
  ES: [
    "Para tu primer libro publicado.",
    "Para autores con un ritmo regular de publicación.",
    "Para self-publishers serios con alta producción.",
  ],
  PT: [
    "Para o seu primeiro livro publicado.",
    "Para autores com ritmo constante de publicação.",
    "Para self-publishers profissionais com alta produção.",
  ],
  NL: [
    "Voor je eerste gepubliceerde boek.",
    "Voor auteurs met een vast publicatieritme.",
    "Voor serieuze self-publishers met hoge output.",
  ],
  SE: [
    "För din första utgivna bok.",
    "För författare med stadig utgivningstakt.",
    "För seriösa self-publishers med högt utgivningstempo.",
  ],
  PL: [
    "Dla Twojej pierwszej publikacji.",
    "Dla autorów regularnie wydających książki.",
    "Dla profesjonalnych self-publisherów wydających dużo książek.",
  ],
};

const buildLandingPlans = (lang) => {
  const code = String(lang || "EN").toUpperCase();
  const cadence = planCadenceForLang(code);
  return [
    {
      id: "basic",
      name: PLAN_NAMES[code]?.[0] || PLAN_NAMES.EN[0],
      price: priceForCard("basic", code, "monthly").price,
      cadence,
      desc: PLAN_DESCRIPTIONS[code]?.[0] || PLAN_DESCRIPTIONS.EN[0],
      featured: false,
      perks: landingPlanPerks("starter", code),
    },
    {
      id: "pro",
      name: PLAN_NAMES[code]?.[1] || PLAN_NAMES.EN[1],
      price: priceForCard("pro", code, "monthly").price,
      cadence,
      desc: PLAN_DESCRIPTIONS[code]?.[1] || PLAN_DESCRIPTIONS.EN[1],
      featured: true,
      perks: landingPlanPerks("author", code),
    },
    {
      id: "publisher",
      name: PLAN_NAMES[code]?.[2] || PLAN_NAMES.EN[2],
      price: priceForCard("publisher", code, "monthly").price,
      cadence,
      desc: PLAN_DESCRIPTIONS[code]?.[2] || PLAN_DESCRIPTIONS.EN[2],
      featured: false,
      perks: landingPlanPerks("pro", code),
    },
  ];
};

const LANDING_STATS_PLAN_LANGS = ["EN", "DA", "DE", "FR", "IT", "ES", "PT", "NL", "SE", "PL"];

const LANGS = [
  { code:"DA", label:"DA", flag:"🇩🇰", name:"Dansk",      speakers:"6M"   },
  { code:"EN", label:"EN", flag:"🇬🇧", name:"English",    speakers:"1.5B" },
  { code:"DE", label:"DE", flag:"🇩🇪", name:"Deutsch",    speakers:"134M" },
  { code:"ES", label:"ES", flag:"🇪🇸", name:"Español",    speakers:"558M" },
  { code:"FR", label:"FR", flag:"🇫🇷", name:"Français",   speakers:"300M" },
  { code:"IT", label:"IT", flag:"🇮🇹", name:"Italiano",   speakers:"85M"  },
  { code:"NL", label:"NL", flag:"🇳🇱", name:"Nederlands", speakers:"25M"  },
  { code:"PL", label:"PL", flag:"🇵🇱", name:"Polski",     speakers:"45M"  },
  { code:"PT", label:"PT", flag:"🇵🇹", name:"Português",  speakers:"266M" },
  { code:"SE", label:"SE", flag:"🇸🇪", name:"Svenska",    speakers:"13M"  },
];
export { LANGS }

const EURO_TRANSLATION_ALIASES = {
  IT: {
    source: "EN",
    tr: {
      nav_transcribe:"Trascrizione", nav_write:"Scrivi e modifica", nav_proofread:"Correzione", nav_cover:"Design copertina", nav_branding:"Marketing", nav_publish:"Pubblica",
      sidebar_book_placeholder:"Libro senza titolo...", sidebar_language:"Lingua", badge:"Scrittura di libri con IA",
      hero_title_1:"La tua storia,", hero_title_2:"raccontata magnificamente.", hero_cta:"✨ Inizia a scrivere gratis", hero_demo:"▶ Scopri come funziona",
      workspace_title:"Area libro", nav_overview:"Panoramica", nav_group_writing:"Processo di scrittura", nav_group_marketing:"Marketing", nav_group_publishing:"Pubblicazione",
      library_title:"La mia libreria", library_save:"Salva libro corrente", library_new:"Nuovo libro", library_empty_title:"Nessun libro salvato", library_load:"Carica", library_delete:"Elimina",
    },
    settings: { settings_title:"Account e impostazioni", settings_language:"Lingua e accessibilità", settings_logout:"Esci", settings_support:"Supporto" },
    author: { by_the_author:"dall'autore" },
  },
  NL: {
    source: "EN",
    tr: {
      nav_transcribe:"Transcriptie", nav_write:"Schrijven & bewerken", nav_proofread:"Proeflezen", nav_cover:"Omslagontwerp", nav_branding:"Marketing", nav_publish:"Publiceren",
      sidebar_book_placeholder:"Naamloos boek...", sidebar_language:"Taal", badge:"AI-gestuurd boeken schrijven",
      hero_title_1:"Jouw verhaal,", hero_title_2:"prachtig verteld.", hero_cta:"✨ Begin gratis met schrijven", hero_demo:"▶ Bekijk hoe het werkt",
      workspace_title:"Boekwerkruimte", nav_overview:"Overzicht", nav_group_writing:"Schrijfproces", nav_group_marketing:"Marketing", nav_group_publishing:"Publiceren",
      library_title:"Mijn bibliotheek", library_save:"Huidig boek opslaan", library_new:"Nieuw boek", library_empty_title:"Nog geen opgeslagen boeken", library_load:"Laden", library_delete:"Verwijderen",
    },
    settings: { settings_title:"Account & instellingen", settings_language:"Taal & toegankelijkheid", settings_logout:"Uitloggen", settings_support:"Support" },
    author: { by_the_author:"door de auteur" },
  },
  SE: {
    source: "EN",
    tr: {
      nav_transcribe:"Transkribering", nav_write:"Skriv & redigera", nav_proofread:"Korrekturläsning", nav_cover:"Omslagsdesign", nav_branding:"Marknadsföring", nav_publish:"Publicera",
      sidebar_book_placeholder:"Namnlös bok...", sidebar_language:"Språk", badge:"AI-driven bokskrivning",
      hero_title_1:"Din berättelse,", hero_title_2:"vackert berättad.", hero_cta:"✨ Börja skriva gratis", hero_demo:"▶ Se hur det fungerar",
      workspace_title:"Bokarbetsyta", nav_overview:"Översikt", nav_group_writing:"Skrivprocess", nav_group_marketing:"Marknadsföring", nav_group_publishing:"Publicering",
      library_title:"Mitt bibliotek", library_save:"Spara aktuell bok", library_new:"Ny bok", library_empty_title:"Inga sparade böcker ännu", library_load:"Ladda", library_delete:"Radera",
    },
    settings: { settings_title:"Konto & inställningar", settings_language:"Språk & tillgänglighet", settings_logout:"Logga ut", settings_support:"Support" },
    author: { by_the_author:"av författaren" },
  },
};

const litraBrandStyle = {
  fontFamily: "inherit",
  fontWeight: "inherit",
  letterSpacing: "inherit",
  textTransform: "uppercase",
  color: C.purple,
};

const renderBrandText = (children, litraStyle = litraBrandStyle) => String(children ?? "")
  .split(/(LITRA!)/g)
  .map((part,i)=>{
    if(part==="LITRA!") return <span key={i} style={litraStyle}>{part}</span>;
    return <span key={i}>{part}</span>;
  });

const BrandedText = ({children}) => renderBrandText(children);
export { BrandedText }

const LANG_SPEAKERS_LABEL = {
  DA: 'talere',
  EN: 'speakers',
  DE: 'Sprecher',
  ES: 'hablantes',
  FR: 'locuteurs',
  IT: 'parlanti',
  NL: 'sprekers',
  PL: 'mówców',
  PT: 'falantes',
  SE: 'talare',
}

export const LangDropdown = ({ lang, setLang, iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const cur = LANGS.find(l => l.code === lang) || LANGS[0];

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = code => { setLang(code); setOpen(false); };
  const speakersLabel = LANG_SPEAKERS_LABEL[normalizeLanguageCode(lang)] || LANG_SPEAKERS_LABEL.EN;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>

      {/* ── Trigger: only shows the active language ── */}
      <button onClick={() => setOpen(o => !o)} style={{
        display: "inline-flex", alignItems: "center", gap: iconOnly ? 0 : 6,
        padding: iconOnly ? "5px 7px" : "6px 12px",
        borderRadius: 100, border: `1.5px solid ${open ? C.purple : C.border}`,
        background: open ? C.purplePale : C.surface, cursor: "pointer",
        transition: "all .18s", boxShadow: "0 1px 4px rgba(0,0,0,.05)",
      }}>
        <span style={{ fontSize: 16, lineHeight: 1 }}>{cur.flag}</span>
        {!iconOnly && <>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{cur.label}</span>
          <span style={{ fontSize: 9, color: C.muted, marginLeft: 2,
            transform: open ? "rotate(180deg)" : "none", transition: "transform .18s",
            display: "inline-block" }}>▼</span>
        </>}
      </button>

      {/* ── Popover panel ── */}
      {open && (
        <div className="language-dropdown" style={{
          position: "absolute", top: "calc(100% + 7px)",
          right: 0, zIndex: 400,
          background: C.surface, border: `1.5px solid ${C.border}`,
          borderRadius: 16, padding: "8px 8px 20px",
          boxShadow: "0 14px 48px rgba(30,42,58,.15)",
          minWidth: 210, animation: "bbFadeUp .16s ease",
        }}>
          {LANGS.map(l => {
            const active = lang === l.code;
            return (
              <div key={l.code} onClick={() => pick(l.code)} style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "8px 10px", borderRadius: 10, cursor: "pointer",
                background: active ? C.purplePale : "transparent",
                border: `1.5px solid ${active ? C.purple + "40" : "transparent"}`,
                marginBottom: 2, transition: "background .14s",
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.pageBg; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{l.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: active ? 600 : 400,
                    color: active ? C.purple : C.text }}>{l.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{l.speakers} {speakersLabel}</div>
                </div>
                {active && <div style={{ width: 16, height: 16, borderRadius: "50%",
                  background: C.purple, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 9, color: "#fff", flexShrink: 0 }}>✓</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const GlobalStyle = ()=>{
  useEffect(()=>{
    if(typeof document==="undefined")return;
    if(document.body.classList.contains("bbg-motion-enabled"))return;
    let enabled=false;
    const enableMotion=()=>{
      if(enabled)return;
      enabled=true;
      document.body.classList.add("bbg-motion-enabled");
      cleanup();
    };
    const cleanup=()=>{
      window.removeEventListener("pointerdown",enableMotion);
      window.removeEventListener("keydown",enableMotion);
      window.removeEventListener("touchstart",enableMotion);
    };
    window.addEventListener("pointerdown",enableMotion,{passive:true});
    window.addEventListener("keydown",enableMotion);
    window.addEventListener("touchstart",enableMotion,{passive:true});
    return cleanup;
  },[]);

  return <style>{`
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{font-size:16px;} body{background:${C.pageBg};color:${C.text};font-family:'Inter',system-ui,sans-serif;-webkit-font-smoothing:antialiased;}
    h1,h2,h3,h4,h5,h6{font-family:'Inter',system-ui,sans-serif;font-weight:600;}
    ::-webkit-scrollbar{width:5px;} ::-webkit-scrollbar-track{background:transparent;}
    ::-webkit-scrollbar-thumb{background:${C.borderMid};border-radius:10px;}

    @keyframes bbFadeUp{from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);}}
    @keyframes bbFadeIn{from{opacity:0;}to{opacity:1;}}
    @keyframes bbSpin{to{transform:rotate(360deg);}}
    @keyframes bbPulse{0%,100%{opacity:1;}50%{opacity:.3;}}
    @keyframes bbShimmer{0%{background-position:-600px 0;}100%{background-position:600px 0;}}
    @keyframes bbFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-7px);}}

    .bb-up,.bb-in,.bb-float,.bb-pulse,.bbg-spinner{animation:none;}
    body.bbg-motion-enabled .bb-up{animation:bbFadeUp .42s cubic-bezier(.22,.68,0,1.15) both;}
    body.bbg-motion-enabled .bb-in{animation:bbFadeIn .28s ease both;}
    body.bbg-motion-enabled .bb-float{animation:bbFloat 4s ease-in-out infinite;}
    body.bbg-motion-enabled .bb-pulse{animation:bbPulse 2s ease infinite;}
    body.bbg-motion-enabled .bbg-spinner{animation:bbSpin .75s linear infinite;}
    .d1{animation-delay:.04s;}.d2{animation-delay:.08s;}.d3{animation-delay:.12s;}
    .d4{animation-delay:.16s;}.d5{animation-delay:.20s;}.d6{animation-delay:.24s;}

    /* Buttons */
    .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;
      padding:10px 22px;border-radius:999px;border:none;cursor:pointer;
      font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;
      transition:all .2s cubic-bezier(.4,0,.2,1);white-space:nowrap;}
    .btn:disabled{opacity:.42;cursor:not-allowed;transform:none!important;}
    .btn-primary{background:linear-gradient(135deg,${C.purple},${C.rose});color:#fff;box-shadow:0 3px 14px ${C.purple}35;}
    .btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 7px 22px ${C.purple}45;}
    .btn-blue{background:${G.blue};color:#fff;box-shadow:0 3px 14px ${C.blue}35;}
    .btn-blue:hover:not(:disabled){transform:translateY(-1px);}
    .btn-ghost{background:${C.surface};color:${C.textMid};border:1px solid #E5E7EB;box-shadow:0 1px 5px rgba(0,0,0,.05);}
    .btn-ghost:hover:not(:disabled){background:${C.purplePale};border-color:${C.purple}50;color:${C.purple};}
    .btn-sm{padding:7px 16px;font-size:13px;}
    .btn-lg{padding:13px 30px;font-size:15.5px;}
    .btn-xl{padding:15px 38px;font-size:17px;}

    /* Matched button pairs (e.g. Gem bog / Ny bog) */
    .btn-pair-outline,.btn.btn-pair-outline,
    .bbg-btn-pair-row>.btn-ghost{
      display:inline-flex;align-items:center;justify-content:center;gap:7px;
      min-height:40px;height:40px;padding:0 18px!important;border-radius:999px!important;
      font-size:14px!important;font-weight:600!important;line-height:1;white-space:nowrap;
      background:${C.surface};color:${C.textMid};border:1.5px solid ${C.purpleLight};
      box-shadow:none;cursor:pointer;transition:all .2s cubic-bezier(.4,0,.2,1);}
    .btn-pair-outline:hover:not(:disabled),
    .bbg-btn-pair-row>.btn-ghost:hover:not(:disabled){
      background:${C.purplePale};border-color:${C.purple};color:${C.purple};}
    .btn-pair-primary,.btn.btn-pair-primary,
    .bbg-btn-pair-row>.btn-primary{
      display:inline-flex;align-items:center;justify-content:center;gap:7px;
      min-height:40px;height:40px;padding:0 18px!important;border-radius:999px!important;
      font-size:14px!important;font-weight:600!important;line-height:1;white-space:nowrap;
      border:none;background:linear-gradient(135deg,${C.purple},${C.rose});color:#fff;
      box-shadow:0 3px 14px ${C.purple}35;cursor:pointer;transition:all .2s cubic-bezier(.4,0,.2,1);}
    .btn-pair-primary:hover:not(:disabled),
    .bbg-btn-pair-row>.btn-primary:hover:not(:disabled){
      transform:translateY(-1px);box-shadow:0 7px 22px ${C.purple}45;}
    .bbg-btn-pair-row{display:flex;gap:8px;flex-wrap:wrap;align-items:center;}
    .bbg-btn-pair-row>.btn{min-height:40px;height:40px;padding:0 18px!important;
      border-radius:999px!important;font-size:14px!important;font-weight:600!important;}

    /* Cards */
    .card{background:${C.surface};border:1px solid rgba(124,111,205,0.14);border-radius:22px;
      padding:20px;box-shadow:0 6px 18px rgba(26,26,62,0.06);transition:box-shadow .2s,border-color .2s,transform .2s;}
    .card-lift:hover{box-shadow:0 22px 48px rgba(26,26,62,0.12);transform:translateY(-2px);}

    /* Inputs */
    .inp{width:100%;background:${C.surface};border:1px solid #E5E7EB;border-radius:16px;
      padding:11px 16px;color:${C.text};font-family:'Inter',system-ui,sans-serif;font-size:14px;
      outline:none;transition:border-color .2s,box-shadow .2s;}
    .inp:focus{border-color:${C.purple};box-shadow:0 0 0 3px ${C.purple}16;}
    .inp::placeholder{color:${C.muted};}
    .ta{width:100%;background:${C.surface};border:1px solid #E5E7EB;border-radius:16px;
      padding:13px 16px;color:${C.text};font-family:'Inter',system-ui,sans-serif;font-size:15px;
      outline:none;transition:border-color .2s,box-shadow .2s;resize:vertical;min-height:110px;line-height:1.75;}
    .ta:focus{border-color:${C.purple};box-shadow:0 0 0 3px ${C.purple}16;}
    .ta::placeholder{color:${C.muted};font-style:italic;}

    /* Progress */
    .prog-track{height:3px;background:${C.purpleLight}50;border-radius:10px;overflow:hidden;}
    .prog-fill{height:100%;background:linear-gradient(90deg,${C.purple},${C.rose});border-radius:10px;transition:width .6s cubic-bezier(.4,0,.2,1);}

    /* Sidebar */
    .sidebar{background:${C.surface};border-right:1.5px solid ${C.border};display:flex;flex-direction:column;
      flex-shrink:0;transition:width .3s cubic-bezier(.4,0,.2,1);overflow:hidden;
      box-shadow:none;}
    .sb-link{display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:12px;
      cursor:pointer;color:${C.muted};font-size:14px;font-weight:500;transition:all .18s;
      border:1.5px solid transparent;margin-bottom:2px;}
    .sb-link:hover{color:${C.text};background:${C.purplePale};}
    .sb-link.active{color:${C.purple};background:${C.purplePale};border-color:${C.purple}30;}
    .sb-text{transition:opacity .15s ease,transform .15s ease;white-space:nowrap;overflow:hidden;}
    .sidebar.col .sb-text{opacity:0;transform:translateX(-6px);pointer-events:none;width:0;}
    .sidebar.col .sb-link{justify-content:center;padding:10px;}
    .col-btn{width:28px;height:28px;border-radius:8px;border:1.5px solid ${C.border};background:${C.pageBg};
      cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:11px;
      color:${C.muted};transition:all .18s;flex-shrink:0;}
    .col-btn:hover{background:${C.purplePale};border-color:${C.purple}50;color:${C.purple};}

    /* Lang grid in sidebar */
    /* lang-bar / lang-grid removed — all language selection is in LangDropdown */
    .language-dropdown{max-height:400px;overflow-y:auto;scroll-padding-bottom:20px;
      scrollbar-width:thin;scrollbar-color:${C.purple} ${C.pageBg};}
    .language-dropdown::-webkit-scrollbar{width:6px;}
    .language-dropdown::-webkit-scrollbar-track{background:${C.pageBg};border-radius:999px;}
    .language-dropdown::-webkit-scrollbar-thumb{background:${C.purple};border-radius:999px;}

    /* Tags */
    .tag{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:100px;font-size:12px;font-weight:500;}
    .tag-purple{background:${C.purplePale};color:${C.purpleDark};border:1px solid ${C.purpleLight};}
    .tag-blue{background:${C.purplePale};color:${C.purpleDark};border:1px solid ${C.purpleLight};}
    .tag-rose{background:${C.rosePale};color:${C.roseDark};border:1px solid ${C.roseLight};}
    .tag-red{background:#FDEEEE;color:${C.danger};border:1px solid #F5CCCC;}
    .tag-green{background:#E6F4F1;color:${C.success};border:1px solid #B3DDD6;}

    /* Chapter row */
    .ch-row{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:12px;
      cursor:pointer;border:1.5px solid transparent;margin-bottom:3px;transition:all .18s;}
    .ch-row:hover{background:${C.purplePale};border-color:${C.purple}20;}
    .ch-row.active{background:${C.purplePale};border-color:${C.purple}40;}

    /* AI bubble */
    .ai-bubble{background:linear-gradient(135deg,${C.purplePale},${C.surface});
      border:1.5px solid ${C.purpleLight};border-radius:20px;padding:20px 24px;
      position:relative;overflow:hidden;}
    .ai-bubble::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;
      background:linear-gradient(90deg,${C.purple},${C.rose},${C.purpleDark});
      background-size:600px 100%;}
    body.bbg-motion-enabled .ai-bubble::before{animation:bbShimmer 3.5s linear infinite;}

    /* Modal */
    .modal-bg{position:fixed;inset:0;background:rgba(30,42,58,.4);backdrop-filter:blur(10px);
      display:flex;align-items:center;justify-content:center;z-index:200;padding:20px;}
    .modal{background:${C.surface};border:1.5px solid ${C.border};border-radius:24px;
      padding:36px;max-width:620px;width:100%;box-shadow:0 24px 80px rgba(124,111,205,.18);}
    body.bbg-motion-enabled .modal-bg{animation:bbFadeIn .2s ease;}
    body.bbg-motion-enabled .modal{animation:bbFadeUp .3s ease;}
    @media (max-width: 767px){
      .bb-up,.bb-in,.bb-float,.bb-pulse,.bbg-spinner,
      .ai-bubble::before,.modal-bg,.modal{animation:none!important;}
      .bbg-coach-loading .bbg-spinner{display:none!important;}
    }

    /* Plan card */
    .plan-card{border:1.5px solid ${C.border};border-radius:20px;padding:28px;
      transition:all .22s;cursor:pointer;position:relative;overflow:hidden;background:${C.surface};}
    .plan-card:hover{border-color:${C.purple}80;box-shadow:0 8px 32px ${C.purple}12;transform:translateY(-2px);}
    .plan-card.sel{border-color:${C.purple};box-shadow:0 8px 32px ${C.purple}18;background:${C.purplePale};}

    /* Q pill */
    .q-pill{display:inline-flex;align-items:center;justify-content:center;
      width:42px;height:42px;border-radius:50%;font-size:15px;font-weight:600;
      border:2px solid ${C.border};background:${C.surface};cursor:pointer;transition:all .18s;color:${C.textMid};}
    .q-pill:hover{border-color:${C.purple};color:${C.purple};background:${C.purplePale};}
    .q-pill.sel{border-color:${C.purple};color:#fff;background:${G.primary};}

    /* Score ring */
    .score-ring{width:80px;height:80px;border-radius:50%;display:flex;align-items:center;
      justify-content:center;font-size:24px;font-weight:700;font-family:var(--bbg-heading-font, 'Inter', system-ui, sans-serif);}

    /* Cover preview */
    .cover-prev{aspect-ratio:2/3;border-radius:16px;overflow:hidden;border:2px solid ${C.border};
      cursor:pointer;transition:all .22s;position:relative;}
    .cover-prev:hover{border-color:${C.purple};transform:scale(1.03);box-shadow:0 12px 40px ${C.purple}20;}
    .cover-prev.sel{border-color:${C.purple};box-shadow:0 12px 40px ${C.purple}28;}

    .serif{font-family:'Poppins',system-ui,sans-serif;font-weight:600;}
    .rtl{direction:rtl;text-align:right;}
  `}</style>;
};

const LANDING_COPY = {
  EN: {
    stats:[["NEW","Just launched"],["100%","Rights Yours"],["< 1 Hour","Delivery"],["10","Languages"]],
    labelHero:"MEET LITRA!",
    heroTitle:["Your Book.","Your Story.","Written by","LITRA!"],
    heroSub:"A complete writing studio for books in any genre—polish, package, and publish in under an hour. You keep every right.",
    writeCta:"Get Started", exploreCta:"See Plans", heroStartCta:"Get Started",
    pricingLabel:"PRICING", pricingTitle:"Choose your author plan", pricingSub:"Every plan includes books, covers, export, and 100% ownership. Upgrade anytime.",
    trust:"✓ 100% ownership · ✓ Just launched · ✓ Ready for Amazon, Apple Books, Google Play",
    whatLabel:"YOUR WRITING STUDIO", whatTitle:"More Than a Writer. A Literary Architect.",
    bullets:["Guides your idea from first spark to finished manuscript","Shapes your book with professional pacing, flow, and clarity","Creates deep characters with goals, conflicts, and real arcs","Edits every manuscript in 3 rigorous rounds","Delivers a complete cover design brief + publish-ready files"],
    howLink:"See How LITRA! Works →", featuresTitle:"What Makes LITRA! Different",
    features:[["📊","Smart Story Guidance","Turns your idea into a polished book with clear direction."],["🏗️","Adaptive Book Flow","Keeps chapters focused, readable, and satisfying from start to finish."],["👤","Deep Characters","Goals, wounds, secrets, arcs. Every protagonist feels real."],["✍️","Triple Edit","Three rounds: developmental, line, proofread. Flawless every time."],["🎨","Cover Blueprint","Detailed visual brief for front, back, spine — designer-ready."],["📱","Publish Ready","Formatted for Amazon KDP, Apple Books, Google Play, Kobo."]],
    howLabel:"How It Works", howTitle:"Idea to Book in 3 Steps",
    steps:[["01","Share Your Vision","Tell LITRA! your topic, genre, and style through a guided conversation."],["02","LITRA! Writes & Edits","Your book is drafted chapter by chapter, refined, and edited in three rounds."],["03","Publish","Get your manuscript + cover brief. Upload anywhere. Keep 100% royalties."]],
    genresTitle:"Every Genre. Professional Grade.",
    genreGroups: buildLandingGenreGroups("EN"),
    plans:[
      {id:"basic",name:"Starter",price:"$11",cadence:"/mo",desc:"For first-time authors ready to publish.",featured:false,perks:landingPlanPerks("starter","EN")},
      {id:"pro",name:"Author",price:"$29",cadence:"/mo",desc:"For authors with a steady publishing rhythm.",featured:true,perks:landingPlanPerks("author","EN")},
      {id:"publisher",name:"Pro",price:"$59",cadence:"/mo",desc:"For serious self-publishers with high output.",featured:false,perks:landingPlanPerks("pro","EN")},
    ],
    featured:"MOST POPULAR", choose:"Choose Plan", pricingLink:"View Full Pricing →",
    finalTitle:"Your Story Starts Here.", finalSub:"Every finished book begins with one decision. Make yours today.", finalTrust:"100% ownership · 0% royalties · 100% yours",
    footerBrand:"Professional book creation with BookBuilderGo.",
    footerCols:[["Product",["How It Works","Pricing","Genres","Languages"]],["Company",["About","Contact","Blog"]],["Support",["FAQ","Help Center","Terms","Privacy"]]],
    socials:"Social links",
  },
  DA: {
    stats:[["NY","Just launched"],["100%","Dine rettigheder"],["< 1 time","Levering"],["10","Sprog"]],
    labelHero:"MØD LITRA!", heroTitle:["Din bog.","Din historie.","Skrevet med","LITRA!"],
    heroSub:"Et komplet forfatterstudio til bøger i alle genrer—finpuds, pak og udgiv på under en time. Du beholder alle rettigheder.",
    writeCta:"Kom i gang", exploreCta:"Se planer", heroStartCta:"Kom i gang",
    pricingLabel:"PRISER", pricingTitle:"Vælg din forfatter-plan", pricingSub:"Alle planer inkluderer bøger, covers, eksport og 100% ejerskab. Opgrader når som helst.",
    trust:"✓ 100% ejerskab · ✓ Just launched · ✓ Klar til Amazon, Apple Books, Google Play",
    whatLabel:"DIT FORFATTERSTUDIO", whatTitle:"Mere end en skriver. En litterær arkitekt.",
    bullets:["Fører din idé fra første gnist til færdigt manuskript","Former bogen med professionelt tempo, flow og klarhed","Skaber dybe karakterer med mål, konflikter og udvikling","Redigerer hvert manuskript i 3 grundige runder","Leverer omslagsbrief og udgivelsesklare filer"],
    howLink:"Se hvordan LITRA! virker →", featuresTitle:"Det der gør LITRA! anderledes",
    features:[["📊","Intelligent fortællehjælp","Gør din idé til en poleret bog med tydelig retning."],["🏗️","Tilpasset bogflow","Holder kapitlerne fokuserede, læsbare og tilfredsstillende."],["👤","Dybe karakterer","Mål, sår, hemmeligheder og udvikling. Hovedpersonen føles virkelig."],["✍️","Tredobbelt redigering","Tre runder: udvikling, linje og korrektur."],["🎨","Omslagsbrief","Detaljeret visuel brief til forside, bagside og ryg."],["📱","Klar til udgivelse","Formateret til Amazon KDP, Apple Books, Google Play og Kobo."]],
    howLabel:"Sådan Virker Det", howTitle:"Fra Idé til Bog på 3 Trin",
    steps:[["01","Del Din Vision","Fortæl LITRA! dit emne, genre og stil gennem en guidet samtale."],["02","LITRA! Skriver & Redigerer","Din bog skrives kapitel for kapitel, forfinet og redigeret i tre runder."],["03","Udgiv","Få dit manuskript + cover brief. Upload overalt. Behold 100% royalties."]],
    genresTitle:"Alle genrer. Professionel kvalitet.",
    genreGroups: buildLandingGenreGroups("DA"),
    plans:[
      {id:"basic",name:"Starter",price:"79 kr",cadence:"/md.",desc:"Til dig der vil udgive din første bog.",featured:false,perks:landingPlanPerks("starter","DA")},
      {id:"pro",name:"Author",price:"199 kr",cadence:"/md.",desc:"Til forfattere med fast udgivelsestempo.",featured:true,perks:landingPlanPerks("author","DA")},
      {id:"publisher",name:"Pro",price:"449 kr",cadence:"/md.",desc:"Til seriøse self-publishers og højt udgivelsestempo.",featured:false,perks:landingPlanPerks("pro","DA")},
    ],
    featured:"MEST POPULÆR", choose:"Vælg plan", pricingLink:"Se alle priser →",
    finalTitle:"Din historie starter her.", finalSub:"Hver færdig bog begynder med én beslutning. Tag den i dag.", finalTrust:"100% ejerskab · 0% royalties · 100% din",
    footerBrand:"Professionel bogskabelse med BookBuilderGo.",
    footerCols:[["Produkt",["Sådan virker det","Priser","Genrer","Sprog"]],["Firma",["Om os","Kontakt","Blog"]],["Support",["FAQ","Hjælp","Vilkår","Privatliv"]]],
    socials:"Sociale links",
  },
};

const LANDING_TRANSLATIONS = {
  ...LANDING_COPY,
  DE: {...LANDING_COPY.EN, labelHero:"LITRA! KENNENLERNEN", heroTitle:["Dein Buch.","Deine Geschichte.","Geschrieben mit","LITRA!"], heroSub:"Dein professionelles Schreibstudio für Bücher in jedem Genre—fertig zum Verfeinern und Veröffentlichen in unter einer Stunde.", writeCta:"Mein Buch schreiben", exploreCta:"Pläne ansehen", heroStartCta:"Loslegen", pricingLabel:"PREISE", pricingTitle:"Wähle deinen Autoren-Plan", pricingSub:"Alle Pläne enthalten Bücher, Cover, Export und 100% Eigentum. Jederzeit upgraden.", trust:"✓ 100% Eigentum · ✓ Soeben veröffentlicht · ✓ Bereit für Amazon, Apple Books, Google Play", whatLabel:"DEIN SCHREIBSTUDIO", whatTitle:"Mehr als ein Schreiber. Ein literarischer Architekt.", howLink:"So funktioniert LITRA! →", featuresTitle:"Was LITRA! besonders macht", howLabel:"SO FUNKTIONIERT ES", howTitle:"Von der Idee zum Buch in 3 Schritten", genresTitle:"Jedes Genre. Professionelle Qualität.", featured:"EMPFOHLEN — BELIEBT", choose:"Plan wählen", pricingLink:"Alle Preise ansehen →", finalTitle:"Deine Geschichte beginnt hier.", finalSub:"Jedes fertige Buch beginnt mit einer Entscheidung. Triff sie heute.", finalTrust:"100% Eigentum · 0% Tantiemen · 100% deins", footerBrand:"Professionelle Bucherstellung mit BookBuilderGo.", footerCols:[["Produkt",["So funktioniert es","Preise","Genres","Sprachen"]],["Unternehmen",["Über uns","Kontakt","Blog"]],["Support",["FAQ","Hilfe","Bedingungen","Datenschutz"]]]},
  FR: {...LANDING_COPY.EN, labelHero:"DÉCOUVRIR LITRA!", heroTitle:["Votre livre.","Votre histoire.","Écrit avec","LITRA!"], heroSub:"Un studio d'écriture complet pour des livres dans tous les genres—prêts à publier en moins d'une heure.", writeCta:"Écrire mon livre", exploreCta:"Voir les offres", heroStartCta:"Commencer", pricingLabel:"TARIFS", pricingTitle:"Choisissez votre plan d'auteur", pricingSub:"Tous les plans incluent livres, couvertures, export et 100% de propriété. Mettez à niveau à tout moment.", trust:"✓ 100% de propriété · ✓ Tout juste lancé · ✓ Prêt pour Amazon, Apple Books, Google Play", whatLabel:"VOTRE STUDIO D'ÉCRITURE", whatTitle:"Plus qu'un rédacteur. Un architecte littéraire.", howLink:"Voir comment LITRA! fonctionne →", featuresTitle:"Ce qui rend LITRA! différent", howLabel:"COMMENT ÇA MARCHE", howTitle:"De l'idée au livre en 3 étapes", genresTitle:"Tous les genres. Qualité professionnelle.", featured:"MIS EN AVANT — POPULAIRE", choose:"Choisir l'offre", pricingLink:"Voir tous les prix →", finalTitle:"Votre histoire commence ici.", finalSub:"Chaque livre terminé commence par une décision. Prenez-la aujourd'hui.", finalTrust:"100% propriété · 0% royalties · 100% à vous", footerBrand:"Création professionnelle de livres avec LITRA!", footerCols:[["Produit",["Fonctionnement","Prix","Genres","Langues"]],["Entreprise",["À propos","Contact","Blog"]],["Support",["FAQ","Aide","Conditions","Confidentialité"]]]},
  ES: {...LANDING_COPY.EN, labelHero:"CONOCE LITRA!", heroTitle:["Tu libro.","Tu historia.","Escrito con","LITRA!"], heroSub:"Un estudio de escritura completo para libros de cualquier género—listos para publicar en menos de una hora.", writeCta:"Escribir mi libro", exploreCta:"Ver planes", heroStartCta:"Empezar", pricingLabel:"PRECIOS", pricingTitle:"Elige tu plan de autor", pricingSub:"Todos los planes incluyen libros, portadas, exportación y 100% de propiedad. Actualiza cuando quieras.", trust:"✓ 100% propiedad · ✓ Recién lanzado · ✓ Listo para Amazon, Apple Books, Google Play", whatLabel:"TU ESTUDIO DE ESCRITURA", whatTitle:"Más que un escritor. Un arquitecto literario.", howLink:"Ver cómo funciona LITRA! →", featuresTitle:"Qué hace diferente a LITRA!", howLabel:"CÓMO FUNCIONA", howTitle:"De idea a libro en 3 pasos", genresTitle:"Todos los géneros. Calidad profesional.", featured:"DESTACADO — MÁS POPULAR", choose:"Elegir plan", pricingLink:"Ver precios →", finalTitle:"Tu historia empieza aquí.", finalSub:"Cada libro terminado empieza con una decisión. Tómala hoy.", finalTrust:"100% propiedad · 0% regalías · 100% tuyo", footerBrand:"Creación profesional de libros con LITRA!", footerCols:[["Producto",["Cómo funciona","Precios","Géneros","Idiomas"]],["Empresa",["Acerca de","Contacto","Blog"]],["Soporte",["FAQ","Ayuda","Términos","Privacidad"]]]},
  PT: {...LANDING_COPY.EN, labelHero:"CONHEÇA A LITRA!", heroTitle:["O seu livro.","A sua história.","Escrito com","LITRA!"], heroSub:"Um estúdio de escrita completo para livros em qualquer género—prontos para publicar em menos de uma hora.", writeCta:"Escrever o meu livro", exploreCta:"Ver planos", heroStartCta:"Começar", pricingLabel:"PREÇOS", pricingTitle:"Escolha seu plano de autor", pricingSub:"Cada plano inclui livros, capas, exportação e 100% de propriedade. Atualize a qualquer momento.", trust:"✓ 100% de propriedade · ✓ Recém-lançado · ✓ Pronto para Amazon, Apple Books, Google Play", whatLabel:"O SEU ESTÚDIO DE ESCRITA", whatTitle:"Mais do que um escritor. Um arquiteto literário.", howLink:"Ver como a LITRA! funciona →", featuresTitle:"O que torna a LITRA! diferente", howLabel:"COMO FUNCIONA", howTitle:"Da ideia ao livro em 3 passos", genresTitle:"Todos os géneros. Qualidade profissional.", featured:"DESTAQUE — MAIS POPULAR", choose:"Escolher plano", pricingLink:"Ver preços →", finalTitle:"A sua história começa aqui.", finalSub:"Cada livro terminado começa com uma decisão. Tome-a hoje.", finalTrust:"100% propriedade · 0% royalties · 100% seu", footerBrand:"Criação profissional de livros com LITRA!", footerCols:[["Produto",["Como funciona","Preços","Géneros","Idiomas"]],["Empresa",["Sobre","Contacto","Blog"]],["Suporte",["FAQ","Ajuda","Termos","Privacidade"]]]},
  PL: {...LANDING_COPY.EN, labelHero:"POZNAJ LITRA!", heroTitle:["Twoja książka.","Twoja historia.","Napisana z","LITRA!"], heroSub:"Kompletne studio pisarskie do książek w każdym gatunku—gotowe do publikacji w mniej niż godzinę.", writeCta:"Napisz moją książkę", exploreCta:"Zobacz plany", heroStartCta:"Zacznij", pricingLabel:"CENNIK", pricingTitle:"Wybierz swój plan autora", pricingSub:"Każdy plan obejmuje książki, okładki, eksport i 100% własności. Możesz zmienić plan w dowolnej chwili.", trust:"✓ 100% własności · ✓ Świeżo uruchomione · ✓ Gotowe na Amazon, Apple Books, Google Play", whatLabel:"TWOJE STUDIO PISARSKIE", whatTitle:"Więcej niż pisarz. Literacki architekt.", howLink:"Zobacz, jak działa LITRA! →", featuresTitle:"Co wyróżnia LITRA!", howLabel:"Jak To Działa", howTitle:"Od Pomysłu Do Książki W 3 Krokach", steps:[["01","Podziel Się Swoją Wizją","Powiedz LITRA! swój temat, gatunek i styl w prowadzonej rozmowie."],["02","LITRA! Pisze I Redaguje","Twoja książka jest tworzona rozdział po rozdziale, dopracowana i zredagowana w trzech rundach."],["03","Opublikuj","Otrzymaj manuskrypt + brief okładki. Prześlij wszędzie. Zachowaj 100% tantiem."]], genresTitle:"Każdy gatunek. Profesjonalna jakość.", featured:"WYRÓŻNIONE — POPULARNE", choose:"Wybierz plan", pricingLink:"Zobacz ceny →", finalTitle:"Twoja historia zaczyna się tutaj.", finalSub:"Każda gotowa książka zaczyna się od jednej decyzji. Podejmij ją dziś.", finalTrust:"100% własności · 0% tantiem · 100% Twoje", footerBrand:"Profesjonalne tworzenie książek z LITRA!", footerCols:[["Produkt",["Jak to działa","Ceny","Gatunki","Języki"]],["Firma",["O nas","Kontakt","Blog"]],["Wsparcie",["FAQ","Pomoc","Warunki","Prywatność"]]]},
  ZH: {...LANDING_COPY.EN, labelHero:"认识 LITRA!", heroTitle:["您的书。","您的故事。","由","LITRA! 写成。"], heroSub:"完整的书写工作室，适用于任何类型的书籍—一小时内即可润色、包装并出版。", writeCta:"写我的书", exploreCta:"查看方案", trust:"✓ 100% 所有权 · ✓ 全新上线 · ✓ 可用于 Amazon、Apple Books、Google Play", whatLabel:"您的写作工作室", whatTitle:"不只是写作者，更是文学架构师。", howLink:"了解 LITRA! 如何工作 →", featuresTitle:"LITRA! 的不同之处", howLabel:"工作方式", howTitle:"3 步从想法到书", genresTitle:"所有类型，专业品质。", featured:"推荐 — 最受欢迎", choose:"选择方案", pricingLink:"查看全部价格 →", finalTitle:"您的故事从这里开始。", finalSub:"每一本完成的书都始于一个决定。今天就开始。", finalTrust:"100% 所有权 · 0% 版税 · 100% 属于您", footerBrand:"使用 BookBuilderGo 专业创建图书。", footerCols:[["产品",["工作方式","价格","类型","语言"]],["公司",["关于","联系","博客"]],["支持",["FAQ","帮助中心","条款","隐私"]]]},
  HI: {...LANDING_COPY.EN, labelHero:"LITRA! से मिलें", heroTitle:["आपकी पुस्तक।","आपकी कहानी।","LITRA! के साथ","लिखी गई।"], heroSub:"किसी भी शैली की पूर्ण पुस्तकों के लिए आपका पेशेवर लेखन स्टूडियो—एक घंटे से कम में प्रकाशन के लिए तैयार।", writeCta:"मेरी पुस्तक लिखें", exploreCta:"प्लान देखें", trust:"✓ 100% स्वामित्व · ✓ अभी लॉन्च हुआ · ✓ Amazon, Apple Books, Google Play के लिए तैयार", whatLabel:"आपका लेखन स्टूडियो", whatTitle:"लेखक से अधिक। एक साहित्यिक वास्तुकार।", howLink:"देखें LITRA! कैसे काम करता है →", featuresTitle:"LITRA! को अलग क्या बनाता है", howLabel:"यह कैसे काम करता है", howTitle:"3 चरणों में विचार से पुस्तक", genresTitle:"हर शैली। पेशेवर गुणवत्ता।", featured:"विशेष — सबसे लोकप्रिय", choose:"प्लान चुनें", pricingLink:"पूरी कीमतें देखें →", finalTitle:"आपकी कहानी यहाँ शुरू होती है।", finalSub:"हर पूरी पुस्तक एक निर्णय से शुरू होती है। आज निर्णय लें।", finalTrust:"100% स्वामित्व · 0% रॉयल्टी · 100% आपका", footerBrand:"BookBuilderGo के साथ पेशेवर पुस्तक निर्माण।", footerCols:[["उत्पाद",["कैसे काम करता है","कीमतें","शैलियाँ","भाषाएँ"]],["कंपनी",["हमारे बारे में","संपर्क","ब्लॉग"]],["सहायता",["FAQ","सहायता केंद्र","शर्तें","गोपनीयता"]]]},
  AR: {...LANDING_COPY.EN, labelHero:"تعرّف على LITRA!", heroTitle:["كتابك.","قصتك.","مكتوبة مع","LITRA!"], heroSub:"استوديو كتابة متكامل لكتب بأي نوع—جاهزة للنشر في أقل من ساعة.", writeCta:"اكتب كتابي", exploreCta:"استعرض الخطط", trust:"✓ ملكية 100% · ✓ أُطلق حديثًا · ✓ جاهز لـ Amazon وApple Books وGoogle Play", whatLabel:"استوديو الكتابة الخاص بك", whatTitle:"أكثر من كاتب. مهندس أدبي.", howLink:"شاهد كيف تعمل LITRA! →", featuresTitle:"ما الذي يجعل LITRA! مختلفة", howLabel:"كيف يعمل", howTitle:"من فكرة إلى كتاب في 3 خطوات", genresTitle:"كل الأنواع. جودة احترافية.", featured:"مميز — الأكثر شعبية", choose:"اختر الخطة", pricingLink:"عرض الأسعار →", finalTitle:"قصتك تبدأ هنا.", finalSub:"كل كتاب مكتمل يبدأ بقرار واحد. اتخذه اليوم.", finalTrust:"ملكية 100% · 0% عوائد · 100% لك", footerBrand:"إنشاء كتب احترافية مع BookBuilderGo.", footerCols:[["المنتج",["كيف يعمل","الأسعار","الأنواع","اللغات"]],["الشركة",["من نحن","اتصال","مدونة"]],["الدعم",["FAQ","مركز المساعدة","الشروط","الخصوصية"]]]},
};

Object.entries(EURO_TRANSLATION_ALIASES).forEach(([code, config]) => {
  const source = config.source;
  LANDING_TRANSLATIONS[code] = { ...(LANDING_TRANSLATIONS[source] || LANDING_TRANSLATIONS.EN) };
});

LANDING_TRANSLATIONS.SE = {
  ...LANDING_TRANSLATIONS.EN,
  labelHero: "MÖT LITRA!",
  heroTitle: ["Din bok.", "Din berättelse.", "Skriven med", "LITRA!"],
  heroSub:
    "Ditt kompletta författarstudio för böcker i alla genrer—redo att publiceras på under en timme.",
  writeCta: "Skriv min bok",
  exploreCta: "Se planer",
  heroStartCta: "Kom igång",
  pricingLabel: "PRISER",
  pricingTitle: "Välj din författarplan",
  pricingSub: "Alla planer inkluderar böcker, omslag, export och 100% ägarskap. Uppgradera när som helst.",
  trust: "✓ 100% ägarskap · ✓ Just lanserad · ✓ Klar för Amazon, Apple Books, Google Play",
  whatLabel: "DITT FÖRFATTARSTUDIO",
  whatTitle: "Mer än en skribent. En litterär arkitekt.",
  bullets: [
    "Vägleder din idé från första gnista till färdigt manuskript",
    "Formar din bok med professionellt tempo, flyt och klarhet",
    "Skapar djupa karaktärer med mål, konflikter och verkliga bågar",
    "Redigerar varje manuskript i 3 rigorösa rundor",
    "Levererar en komplett omslagsbrief + publiceringsklara filer",
  ],
  howLink: "Se hur LITRA! fungerar →",
  featuresTitle: "Det som gör LITRA! unikt",
  features: [
    ["📊", "Smart berättarguidning", "Förvandlar din idé till en putsad bok med tydlig riktning."],
    ["🏗️", "Adaptiv bokflöde", "Håller kapitel fokuserade, läsbara och tillfredsställande från början till slut."],
    ["👤", "Djupa karaktärer", "Mål, sår, hemligheter, bågar. Varje protagonist känns verklig."],
    ["✍️", "Trippel redigering", "Tre rundor: utveckling, linje, korrekturläsning. Felfri varje gång."],
    ["🎨", "Omslagsbrief", "Detaljerad visuell brief för fram-, baksida, rygg — redo för designer."],
    ["📱", "Publiceringsklar", "Formaterad för Amazon KDP, Apple Books, Google Play, Kobo."],
  ],
  howLabel: "SÅ FUNGERAR DET",
  howTitle: "Från idé till bok på 3 steg",
  genresTitle: "Alla genrer. Professionell kvalitet.",
  featured: "UTVALD — MEST POPULÄR",
  choose: "Välj plan",
  pricingLink: "Se alla priser →",
  finalTitle: "Din berättelse börjar här.",
  finalSub: "Varje färdig bok börjar med ett beslut. Ta det i dag.",
  finalTrust: "100% äganderätt · 0% royalties · 100% ditt",
  footerBrand: "Professionell bokskapning med BookBuilderGo.",
};

LANDING_TRANSLATIONS.IT = {
  ...LANDING_TRANSLATIONS.EN,
  labelHero: "SCOPRI LITRA!",
  heroTitle: ["Il tuo libro.","La tua storia.","Scritto con","LITRA!"],
  heroSub: "Il tuo studio di scrittura professionale per libri di qualsiasi genere—pronto per la pubblicazione in meno di un'ora. Tu mantieni ogni diritto.",
  writeCta: "Scrivi il mio libro",
  exploreCta: "Vedi i piani",
  heroStartCta: "Inizia",
  pricingLabel: "PREZZI",
  pricingTitle: "Scegli il tuo piano per autori",
  pricingSub: "Tutti i piani includono libri, copertine, esportazione e 100% di proprietà. Aggiorna in qualsiasi momento.",
  trust: "✓ 100% di proprietà · ✓ Appena lanciato · ✓ Pronto per Amazon, Apple Books, Google Play",
  whatLabel: "IL TUO STUDIO DI SCRITTURA",
  whatTitle: "Più di uno scrittore. Un architetto letterario.",
  bullets: [
    "Guida la tua idea dalla prima scintilla al manoscritto finito",
    "Plasma il tuo libro con ritmo, fluidità e chiarezza professionali",
    "Crea personaggi profondi con obiettivi, conflitti e archi reali",
    "Revisiona ogni manoscritto in 3 rigorosi round",
    "Fornisce una brief di copertina completa + file pronti per la pubblicazione"
  ],
  howLink: "Scopri come funziona LITRA! →",
  featuresTitle: "Cosa rende LITRA! diverso",
  features: [
    ["📊","Guida narrativa intelligente","Trasforma la tua idea in un libro curato con direzione chiara."],
    ["🏗️","Flusso adattivo","Mantiene i capitoli focalizzati, leggibili e soddisfacenti dall'inizio alla fine."],
    ["👤","Personaggi profondi","Obiettivi, ferite, segreti, archi. Ogni protagonista sembra reale."],
    ["✍️","Tripla revisione","Tre round: sviluppo, line editing, correzione di bozze. Impeccabile ogni volta."],
    ["🎨","Brief di copertina","Brief visivo dettagliato per fronte, retro, dorso — pronto per il designer."],
    ["📱","Pronto per la pubblicazione","Formattato per Amazon KDP, Apple Books, Google Play, Kobo."]
  ],
  howLabel: "Come funziona",
  howTitle: "Da idea a libro in 3 passaggi",
  steps: [
    ["01","Condividi la tua visione","Racconta a LITRA! il tuo argomento, genere e stile attraverso una conversazione guidata."],
    ["02","LITRA! scrive & revisiona","Il tuo libro viene redatto capitolo per capitolo, rifinito e revisionato in tre round."],
    ["03","Pubblica","Ricevi il manoscritto + brief di copertina. Carica ovunque. Tieni il 100% dei royalty."]
  ],
  genresTitle: "Ogni genere. Qualità professionale.",
  featured: "PIÙ POPOLARE",
  choose: "Scegli piano",
  pricingLink: "Vedi tutti i prezzi →",
  finalTitle: "La tua storia inizia qui.",
  finalSub: "Ogni libro finito inizia con una decisione. Fai la tua oggi.",
  finalTrust: "100% proprietà · 0% royalty · 100% tuo",
  footerBrand: "Creazione professionale di libri con BookBuilderGo.",
  footerCols: [
    ["Prodotto",["Come funziona","Prezzi","Generi","Lingue"]],
    ["Azienda",["Chi siamo","Contatti","Blog"]],
    ["Supporto",["FAQ","Centro assistenza","Termini","Privacy"]]
  ],
};

LANDING_TRANSLATIONS.NL = {
  ...LANDING_TRANSLATIONS.EN,
  labelHero: "ONTDEK LITRA!",
  heroTitle: ["Jouw boek.","Jouw verhaal.","Geschreven met","LITRA!"],
  heroSub: "Jouw professionele schrijfstudio voor boeken in elk genre—klaar voor publicatie in minder dan een uur. Jij behoudt alle rechten.",
  writeCta: "Schrijf mijn boek",
  exploreCta: "Bekijk plannen",
  heroStartCta: "Starten",
  pricingLabel: "PRIJZEN",
  pricingTitle: "Kies je auteursplan",
  pricingSub: "Elk plan bevat boeken, omslagen, export en 100% eigendom. Upgrade wanneer je wilt.",
  trust: "✓ 100% eigendom · ✓ Net gelanceerd · ✓ Klaar voor Amazon, Apple Books, Google Play",
  whatLabel: "JOUW SCHRIJFSTUDIO",
  whatTitle: "Meer dan een schrijver. Een literair architect.",
  bullets: [
    "Begeleidt je idee van eerste vonk tot voltooid manuscript",
    "Vormt je boek met professioneel tempo, flow en helderheid",
    "Creëert diepe personages met doelen, conflicten en echte bogen",
    "Bewerkt elk manuscript in 3 rigoureuze rondes",
    "Levert een complete omslagbrief + publicatieklaare bestanden"
  ],
  howLink: "Zie hoe LITRA! werkt →",
  featuresTitle: "Wat maakt LITRA! anders",
  features: [
    ["📊","Slimme verhaalbegeleiding","Verandert je idee in een gepolijst boek met duidelijke richting."],
    ["🏗️","Adaptieve boekflow","Houdt hoofdstukken gefocust, leesbaar en bevredigend van begin tot eind."],
    ["👤","Diepe personages","Doelen, wonden, geheimen, bogen. Elke protagonist voelt echt."],
    ["✍️","Driedubbele edit","Drie rondes: ontwikkeling, lijn, proeflezen. Vlekkeloos elke keer."],
    ["🎨","Omslagblueprint","Gedetailleerde visuele brief voor voor-, achterzijde, rug — klaar voor ontwerper."],
    ["📱","Publicatieklaar","Geformatteerd voor Amazon KDP, Apple Books, Google Play, Kobo."]
  ],
  howLabel: "Hoe het werkt",
  howTitle: "Van idee tot boek in 3 stappen",
  steps: [
    ["01","Deel je visie","Vertel LITRA! je onderwerp, genre en stijl via een begeleid gesprek."],
    ["02","LITRA! schrijft & editeert","Je boek wordt hoofdstuk voor hoofdstuk opgesteld, verfijnd en in drie rondes geëditeerd."],
    ["03","Publiceer","Krijg je manuscript + omslagbrief. Upload overal. Houd 100% royalties."]
  ],
  genresTitle: "Elk genre. Professionele kwaliteit.",
  featured: "MEEST POPULAIR",
  choose: "Kies plan",
  pricingLink: "Bekijk alle prijzen →",
  finalTitle: "Jouw verhaal begint hier.",
  finalSub: "Elk voltooid boek begint met één beslissing. Neem die vandaag.",
  finalTrust: "100% eigendom · 0% royalties · 100% van jou",
  footerBrand: "Professionele boekcreatie met BookBuilderGo.",
  footerCols: [
    ["Product",["Hoe het werkt","Prijzen","Genres","Talen"]],
    ["Bedrijf",["Over ons","Contact","Blog"]],
    ["Support",["FAQ","Helpcentrum","Voorwaarden","Privacy"]]
  ],
};

const PUBLIC_LANDING_TRANSLATIONS = {
  DE: {
    features: [
      ["📊","Intelligente Story-Führung","Verwandelt deine Idee in ein poliertes Buch mit klare Richtung."],
      ["🏗️","Adaptiver Buchfluss","Hält Kapitel fokussiert, lesbar und befriedigend von Anfang bis Ende."],
      ["👤","Tiefe Charaktere","Ziele, Wunden, Geheimnisse, Bögen. Jeder Protagonist fühlt sich echt an."],
      ["✍️","Dreifache Bearbeitung","Drei Runden: Entwicklung, Lektorat, Korrektur. Jedes Mal fehlerfrei."],
      ["🎨","Cover-Briefing","Detaillierter visueller Brief für Vorderseite, Rückseite, Buchrücken — bereit für Designer."],
      ["📱","Veröffentlichungsfertig","Formatiert für Amazon KDP, Apple Books, Google Play, Kobo."],
    ],
    bullets: [
      "Leitet deine Idee vom ersten Funken zum fertigen Manuskript",
      "Formt dein Buch mit professionellem Tempo, Fluss und Klarheit",
      "Erschafft tiefe Charaktere mit Zielen, Konflikten und echten Bögen",
      "Bearbeitet jedes Manuskript in 3 rigorosen Runden",
      "Liefert ein komplettes Cover-Briefing + veröffentlichungsfertige Dateien",
    ],
    steps: [
      ["01","Teile deine Vision","Erzähle LITRA! von deinem Thema, Genre und Stil in einem geführten Gespräch."],
      ["02","LITRA! schreibt & bearbeitet","Dein Buch wird Kapitel für Kapitel geschrieben, verfeinert und in drei Runden bearbeitet."],
      ["03","Veröffentlichen","Erhalte dein Manuskript + Cover-Briefing. Lade überall hoch. 100% Royalties."],
    ],
    pricingTitle: "Wähle deinen Autorenplan",
    pricingSub: "Jeder Plan beinhaltet Bücher, Cover, Export und 100% Eigentum. Upgrade jederzeit.",
  },
  FR: {
    features: [
      ["📊","Guidage narratif intelligent","Transforme ton idée en un livre soigné avec une direction claire."],
      ["🏗️","Flux de livre adaptatif","Maintient les chapitres concentrés, lisibles et satisfaisants du début à la fin."],
      ["👤","Personnages profonds","Objectifs, blessures, secrets, arcs. Chaque protagoniste semble réel."],
      ["✍️","Édition triple","Trois tours : développement, révision, relecture. Impeccable à chaque fois."],
      ["🎨","Brief de couverture","Brief visuel détaillé pour l'avant, l'arrière, le dos — prêt pour le designer."],
      ["📱","Prêt à publier","Formaté pour Amazon KDP, Apple Books, Google Play, Kobo."],
    ],
    bullets: [
      "Guide ton idée de la première étincelle au manuscrit terminé",
      "Façonne ton livre avec un rythme, un flux et une clarté professionnels",
      "Crée des personnages profonds avec des objectifs, des conflits et des arcs réels",
      "Révise chaque manuscrit en 3 tours rigoureux",
      "Fournit un brief de couverture complet + fichiers prêts à publier",
    ],
    steps: [
      ["01","Partage ta vision","Parle à LITRA! de ton sujet, genre et style dans une conversation guidée."],
      ["02","LITRA! écrit & édite","Ton livre est écrit chapitre par chapitre, peaufiné et édité en trois tours."],
      ["03","Publier","Reçois ton manuskript + brief de couverture. Télécharge partout. 100% royalties."],
    ],
    pricingTitle: "Choisis ton plan d'auteur",
    pricingSub: "Chaque plan inclut des livres, des couvertures, l'export et 100% de propriété. Améliore à tout moment.",
  },
  ES: {
    features: [
      ["📊","Guía narrativa inteligente","Transforma tu idea en un libro pulido con dirección clara."],
      ["🏗️","Flujo de libro adaptativo","Mantiene los capítulos enfocados, legibles y satisfactorios de principio a fin."],
      ["👤","Personajes profundos","Metas, heridas, secretos, arcos. Cada protagonista parece real."],
      ["✍️","Edición triple","Tres rondas: desarrollo, edición, corrección. Impecable cada vez."],
      ["🎨","Brief de portada","Brief visual detallado para frente, contraportada, lomo — listo para el diseñador."],
      ["📱","Listo para publicar","Formateado para Amazon KDP, Apple Books, Google Play, Kobo."],
    ],
    bullets: [
      "Guía tu idea de la primera chispa al manuscrito terminado",
      "Moldea tu libro con ritmo, fluidez y claridad profesionales",
      "Crea personajes profundos con objetivos, conflictos y arcos reales",
      "Revisa cada manuscrito en 3 rigurosas rondas",
      "Proporciona un brief de portada completo + archivos listos para publicar",
    ],
    steps: [
      ["01","Comparte tu visión","Cuéntale a LITRA! sobre tu tema, género y estilo en una conversación guiada."],
      ["02","LITRA! escribe & edita","Tu libro se escribe capítulo por capítulo, pulido y editado en tres rondas."],
      ["03","Publicar","Recibe tu manuscrito + brief de portada. Sube a cualquier parte. 100% regalías."],
    ],
    pricingTitle: "Elige tu plan de autor",
    pricingSub: "Todos los planes incluyen libros, portadas, exportación y 100% de propiedad. Actualiza cuando quieras.",
  },
  PT: {
    features: [
      ["📊","Orientação narrativa inteligente","Transforma sua ideia em um livro polido com direção clara."],
      ["🏗️","Fluxo de livro adaptativo","Mantém capítulos focados, legíveis e satisfatórios do início ao fim."],
      ["👤","Personagens profundos","Objetivos, feridas, segredos, arcos. Cada protagonista parece real."],
      ["✍️","Edição tripla","Três rodadas: desenvolvimento, edição, revisão. Impecável sempre."],
      ["🎨","Brief da capa","Brief visual detalhado para frente, verso, lombo — pronto para o designer."],
      ["📱","Pronto para publicar","Formatado para Amazon KDP, Apple Books, Google Play, Kobo."],
    ],
    bullets: [
      "Guia sua ideia da primeira faísca ao manuscrito terminado",
      "Molda seu livro com ritmo, fluxo e clareza profissionais",
      "Cria personagens profundos com objetivos, conflitos e arcos reais",
      "Revisa cada manuscrito em 3 rigorosas rodadas",
      "Entrega um brief de capa completo + arquivos prontos para publicar",
    ],
    steps: [
      ["01","Compartilhe sua visão","Conte ao LITRA! sobre seu tema, gênero e estilo em uma conversa guiada."],
      ["02","LITRA! escreve & edita","Seu livro é escrito capítulo por capítulo, aprimorado e editado em três rodadas."],
      ["03","Publicar","Receba seu manuscrito + brief da capa. Faça upload em qualquer lugar. 100% royalties."],
    ],
    pricingTitle: "Escolha seu plano de autor",
    pricingSub: "Cada plano inclui livros, capas, exportação e 100% de propriedade. Atualize a qualquer momento.",
  },
  PL: {
    features: [
      ["📊","Inteligentne prowadzenie fabuły","Przekształca twój pomysł w dopracowaną książkę z jasnym kierunkiem."],
      ["🏗️","Adaptacyjny przepływ książki","Utrzymuje rozdziały skupione, czytelne i satysfakcjonujące od początku do końca."],
      ["👤","Głębokie postacie","Cele, rany, sekrety, łuki. Każdy protagonist wydaje się prawdziwy."],
      ["✍️","Potrójna edycja","Trzy rundy: rozwój, redakcja, korekta. Bez zarzutu za każdym razem."],
      ["🎨","Brief okładki","Szczegółowy brief wizualny dla przodu, tyłu, grzbietu — gotowy dla projektanta."],
      ["📱","Gotowe do publikacji","Sformatowane dla Amazon KDP, Apple Books, Google Play, Kobo."],
    ],
    bullets: [
      "Prowadzi twój pomysł od pierwszej iskry do gotowego manuskryptu",
      "Kształtuje twoją książkę profesjonalnym tempem, płynnością i jasnością",
      "Tworzy głębokie postacie z celami, konfliktami i prawdziwymi łukami",
      "Redaguje każdy manuskrypt w 3 rygorystycznych rundach",
      "Dostarcza kompletny brief okładki + pliki gotowe do publikacji",
    ],
    steps: [
      ["01","Podziel się swoją wizją","Opowiedz LITRA! o swoim temacie, gatunku i stylu w prowadzonej rozmowie."],
      ["02","LITRA! pisze & redaguje","Twoja książka jest pisana rozdział po rozdziale, dopracowywana i redagowana w trzech rundach."],
      ["03","Publikuj","Otrzymaj swój manuskrypt + brief okładki. Prześlij wszędzie. 100% tantiem."],
    ],
    pricingTitle: "Wybierz swój plan autorski",
    pricingSub: "Każdy plan obejmuje książki, okładki, eksport i 100% własności. Ulepszaj w dowolnym momencie.",
  },
  SE: {
    steps: [
      ["01","Dela din vision","Berätta för LITRA! om ditt ämne, genre och stil i ett guidat samtal."],
      ["02","LITRA! skriver & redigerar","Din bok skrivs kapitel för kapitel, finslipas och redigeras i tre rundor."],
      ["03","Publicera","Få ditt manuskript + omslagsbrief. Ladda upp var som helst. Behåll 100% royalties."],
    ],
  },
};

Object.entries(PUBLIC_LANDING_TRANSLATIONS).forEach(([code, patch]) => {
  LANDING_TRANSLATIONS[code] = {
    ...(LANDING_TRANSLATIONS[code] || LANDING_TRANSLATIONS.EN),
    ...patch,
  };
});

LANDING_STATS_PLAN_LANGS.forEach((code) => {
  LANDING_TRANSLATIONS[code] = {
    ...(LANDING_TRANSLATIONS[code] || LANDING_TRANSLATIONS.EN),
    stats: buildLandingStats(code),
    plans: buildLandingPlans(code),
    genreGroups: buildLandingGenreGroups(code),
  };
});

const LANDING_INCOME_TRANSLATIONS = {
  EN:{label:"AFTER PUBLISHING",title:"From Book to Income",body:"After publishing: LITRA! coaches you toward sales and income.",points:["Daily sales tasks","Amazon keyword and review guidance","Streaks, reminders, and follow-up"]},
  ZH:{label:"发布之后",title:"从图书到收入",body:"发布后：LITRA! 指导你获得销售和收入。",points:["每日销售任务","Amazon 关键词和评论指导","连续行动、提醒和跟进"]},
  HI:{label:"प्रकाशन के बाद",title:"पुस्तक से आय तक",body:"प्रकाशन के बाद: LITRA! आपको बिक्री और आय की ओर कोच करता है।",points:["दैनिक बिक्री कार्य","Amazon कीवर्ड और समीक्षा मार्गदर्शन","स्ट्रीक, रिमाइंडर और फॉलो-अप"]},
  ES:{label:"DESPUÉS DE PUBLICAR",title:"Del libro al ingreso",body:"Después de publicar: LITRA! te guía hacia ventas e ingresos.",points:["Tareas diarias de venta","Guía de keywords y reseñas en Amazon","Rachas, recordatorios y seguimiento"]},
  AR:{label:"بعد النشر",title:"من الكتاب إلى الدخل",body:"بعد النشر: ترشدك LITRA! نحو المبيعات والدخل.",points:["مهام بيع يومية","إرشاد لكلمات Amazon والمراجعات","سلاسل إنجاز وتذكيرات ومتابعة"]},
  FR:{label:"APRÈS PUBLICATION",title:"Du livre au revenu",body:"Après publication : LITRA! vous coache vers les ventes et les revenus.",points:["Tâches de vente quotidiennes","Guidance mots-clés Amazon et avis","Séries, rappels et suivi"]},
  PT:{label:"DEPOIS DA PUBLICAÇÃO",title:"Do livro ao rendimento",body:"Depois de publicar: a LITRA! orienta-o para vendas e rendimento.",points:["Tarefas diárias de vendas","Orientação para keywords e avaliações na Amazon","Sequências, lembretes e acompanhamento"]},
  DE:{label:"NACH DER VERÖFFENTLICHUNG",title:"Vom Buch zum Einkommen",body:"Nach der Veröffentlichung coacht dich LITRA! zu Verkäufen und Einkommen.",points:["Tägliche Verkaufsaufgaben","Amazon-Keyword- und Rezensionshilfe","Streaks, Erinnerungen und Follow-up"]},
  DA:{label:"EFTER UDGIVELSE",title:"Fra Bog til Indtægt",body:"Efter udgivelse: LITRA! coacher dig til salg & indtjening.",points:["Daglige salgsopgaver","Amazon-keywords og anmeldelsesguide","Streaks, påmindelser og opfølgning"]},
  PL:{label:"PO PUBLIKACJI",title:"Od książki do dochodu",body:"Po publikacji: LITRA! prowadzi Cię do sprzedaży i przychodów.",points:["Codzienne zadania sprzedażowe","Wskazówki Amazon keywords i recenzji","Serie, przypomnienia i follow-up"]},
  IT:{label:"DOPO LA PUBBLICAZIONE",title:"Dal Libro al Reddito",body:"Dopo la pubblicazione: LITRA! ti guida verso vendite e reddito.",points:["Compiti di vendita giornalieri","Guida keywords e recensioni Amazon","Serie, promemoria e follow-up"]},
  NL:{label:"NA PUBLICATIE",title:"Van Boek naar Inkomen",body:"Na publicatie: LITRA! coacht je naar verkoop en inkomen.",points:["Dagelijkse verkooptaken","Amazon keyword- en reviewbegeleiding","Reeksen, herinneringen en follow-up"]},
  SE:{label:"EFTER PUBLICERING",title:"Från Bok till Inkomst",body:"Efter publicering: LITRA! coachar dig mot försäljning och inkomst.",points:["Dagliga försäljningsuppgifter","Amazon keyword- och reviewvägledning","Streaks, påminnelser och uppföljning"]},
};

const PUBLIC_LANGS = LANGS;
const setPreferredLanguage = (code, setLang) => {
  const normalized = normalizeLanguageCode(code);
  setLang(normalized);
  try {
    localStorage.setItem("bbg-language", normalized);
    document.cookie = `bbg-language=${normalized}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {}
};

const PUBLIC_UI = {
  EN:{login:"Log In",getStarted:"Get Started",dashboard:"Dashboard",contact:"Contact",messageSent:"Message sent",thankYou:"Thank you!",contactSuccess:"Thank you! We've received your message and will respond within 24 hours.",sendMessage:"Send us a message",fullName:"Full Name",email:"Email",subject:"Subject",message:"Message",send:"Send Message",languageLabel:"Languages",chooseLanguage:"Choose language",helpCenter:"Help Center",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach is available in your dashboard after login.",gotIt:"Got it",legal:"Legal",gdpr:"GDPR",lastUpdated:"Last updated: 17 May 2026"},
  ZH:{login:"登录",getStarted:"开始使用",dashboard:"控制台",contact:"联系",messageSent:"消息已发送",thankYou:"谢谢！",contactSuccess:"谢谢！我们已收到您的消息，并会在 24 小时内回复。",sendMessage:"给我们留言",fullName:"全名",email:"电子邮箱",subject:"主题",message:"消息",send:"发送消息",languageLabel:"语言",chooseLanguage:"选择语言",helpCenter:"帮助中心",coach:"LITRA! 教练",coachUnavailable:"登录后可在控制台使用 LITRA! 教练。",gotIt:"知道了",legal:"法律",gdpr:"GDPR",lastUpdated:"最后更新：2026年5月17日"},
  HI:{login:"लॉग इन",getStarted:"शुरू करें",dashboard:"डैशबोर्ड",contact:"संपर्क",messageSent:"संदेश भेजा गया",thankYou:"धन्यवाद!",contactSuccess:"धन्यवाद! हमें आपका संदेश मिल गया है और हम 24 घंटे में जवाब देंगे।",sendMessage:"हमें संदेश भेजें",fullName:"पूरा नाम",email:"ईमेल",subject:"विषय",message:"संदेश",send:"संदेश भेजें",languageLabel:"भाषाएँ",chooseLanguage:"भाषा चुनें",helpCenter:"सहायता केंद्र",coach:"LITRA! कोच",coachUnavailable:"लॉगिन के बाद LITRA! Coach आपके डैशबोर्ड में उपलब्ध है।",gotIt:"समझ गया",legal:"कानूनी",gdpr:"GDPR",lastUpdated:"अंतिम अपडेट: 17 मई 2026"},
  ES:{login:"Iniciar sesión",getStarted:"Empezar",dashboard:"Panel",contact:"Contacto",messageSent:"Mensaje enviado",thankYou:"¡Gracias!",contactSuccess:"¡Gracias! Hemos recibido tu mensaje y responderemos en 24 horas.",sendMessage:"Envíanos un mensaje",fullName:"Nombre completo",email:"Email",subject:"Asunto",message:"Mensaje",send:"Enviar mensaje",languageLabel:"Idiomas",chooseLanguage:"Elegir idioma",helpCenter:"Centro de ayuda",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach está disponible en tu panel después de iniciar sesión.",gotIt:"Entendido",legal:"Legal",gdpr:"GDPR",lastUpdated:"Última actualización: 17 mayo 2026"},
  AR:{login:"تسجيل الدخول",getStarted:"ابدأ",dashboard:"لوحة التحكم",contact:"اتصال",messageSent:"تم إرسال الرسالة",thankYou:"شكراً!",contactSuccess:"شكراً! تلقينا رسالتك وسنرد خلال 24 ساعة.",sendMessage:"أرسل لنا رسالة",fullName:"الاسم الكامل",email:"البريد الإلكتروني",subject:"الموضوع",message:"الرسالة",send:"إرسال الرسالة",languageLabel:"اللغات",chooseLanguage:"اختر اللغة",helpCenter:"مركز المساعدة",coach:"مدرب LITRA!",coachUnavailable:"يتوفر مدرب LITRA! في لوحة التحكم بعد تسجيل الدخول.",gotIt:"حسناً",legal:"قانوني",gdpr:"GDPR",lastUpdated:"آخر تحديث: 17 مايو 2026"},
  FR:{login:"Connexion",getStarted:"Commencer",dashboard:"Tableau de bord",contact:"Contact",messageSent:"Message envoyé",thankYou:"Merci !",contactSuccess:"Merci ! Nous avons reçu votre message et répondrons sous 24 heures.",sendMessage:"Envoyez-nous un message",fullName:"Nom complet",email:"Email",subject:"Sujet",message:"Message",send:"Envoyer le message",languageLabel:"Langues",chooseLanguage:"Choisir la langue",helpCenter:"Centre d'aide",coach:"Coach LITRA!",coachUnavailable:"Le Coach LITRA! est disponible dans votre tableau de bord après connexion.",gotIt:"Compris",legal:"Mentions légales",gdpr:"RGPD",lastUpdated:"Dernière mise à jour : 17 mai 2026"},
  PT:{login:"Entrar",getStarted:"Começar",dashboard:"Painel",contact:"Contacto",messageSent:"Mensagem enviada",thankYou:"Obrigado!",contactSuccess:"Obrigado! Recebemos a sua mensagem e responderemos em 24 horas.",sendMessage:"Envie-nos uma mensagem",fullName:"Nome completo",email:"Email",subject:"Assunto",message:"Mensagem",send:"Enviar mensagem",languageLabel:"Idiomas",chooseLanguage:"Escolher idioma",helpCenter:"Centro de ajuda",coach:"Coach LITRA!",coachUnavailable:"O Coach LITRA! está disponível no painel após iniciar sessão.",gotIt:"Entendi",legal:"Legal",gdpr:"GDPR",lastUpdated:"Última atualização: 17 maio 2026"},
  DE:{login:"Einloggen",getStarted:"Loslegen",dashboard:"Dashboard",contact:"Kontakt",messageSent:"Nachricht gesendet",thankYou:"Danke!",contactSuccess:"Danke! Wir haben deine Nachricht erhalten und antworten innerhalb von 24 Stunden.",sendMessage:"Schreib uns eine Nachricht",fullName:"Vollständiger Name",email:"E-Mail",subject:"Betreff",message:"Nachricht",send:"Nachricht senden",languageLabel:"Sprachen",chooseLanguage:"Sprache wählen",helpCenter:"Hilfe-Center",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach ist nach dem Login in deinem Dashboard verfügbar.",gotIt:"Verstanden",legal:"Rechtliches",gdpr:"DSGVO",lastUpdated:"Zuletzt aktualisiert: 17. Mai 2026"},
  DA:{login:"Log ind",getStarted:"Kom i gang",dashboard:"Dashboard",contact:"Kontakt",messageSent:"Besked sendt",thankYou:"Tak!",contactSuccess:"Tak! Vi har modtaget din besked og svarer inden for 24 timer.",sendMessage:"Send os en besked",fullName:"Fulde navn",email:"Email",subject:"Emne",message:"Besked",send:"Send besked",languageLabel:"Sprog",chooseLanguage:"Vælg sprog",helpCenter:"Hjælpecenter",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach er tilgængelig i dit dashboard efter login.",gotIt:"Forstået",legal:"Jura",gdpr:"GDPR",lastUpdated:"Sidst opdateret: 17. maj 2026"},
  PL:{login:"Zaloguj",getStarted:"Zacznij",dashboard:"Panel",contact:"Kontakt",messageSent:"Wiadomość wysłana",thankYou:"Dziękujemy!",contactSuccess:"Dziękujemy! Otrzymaliśmy wiadomość i odpowiemy w ciągu 24 godzin.",sendMessage:"Wyślij nam wiadomość",fullName:"Imię i nazwisko",email:"Email",subject:"Temat",message:"Wiadomość",send:"Wyślij wiadomość",languageLabel:"Języki",chooseLanguage:"Wybierz język",helpCenter:"Centrum pomocy",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach jest dostępny w panelu po zalogowaniu.",gotIt:"Rozumiem",legal:"Prawne",gdpr:"RODO",lastUpdated:"Ostatnia aktualizacja: 17 maja 2026"},
};

const CONTACT_SUBJECTS = {
  EN:["General Inquiry","Billing Support","Technical Issue","Partnership","Other"],
  ZH:["一般咨询","账单支持","技术问题","合作","其他"],
  HI:["सामान्य प्रश्न","बिलिंग सहायता","तकनीकी समस्या","साझेदारी","अन्य"],
  ES:["Consulta general","Soporte de facturación","Problema técnico","Colaboración","Otro"],
  AR:["استفسار عام","دعم الفوترة","مشكلة تقنية","شراكة","أخرى"],
  FR:["Demande générale","Support facturation","Problème technique","Partenariat","Autre"],
  PT:["Pedido geral","Suporte de faturação","Problema técnico","Parceria","Outro"],
  DE:["Allgemeine Anfrage","Abrechnungssupport","Technisches Problem","Partnerschaft","Sonstiges"],
  DA:["Generel henvendelse","Fakturasupport","Teknisk problem","Partnerskab","Andet"],
  PL:["Pytanie ogólne","Wsparcie rozliczeń","Problem techniczny","Partnerstwo","Inne"],
};

Object.entries(EURO_TRANSLATION_ALIASES).forEach(([code, config]) => {
  const source = config.source;
  PUBLIC_UI[code] = PUBLIC_UI[source] || PUBLIC_UI.EN;
  CONTACT_SUBJECTS[code] = CONTACT_SUBJECTS[source] || CONTACT_SUBJECTS.EN;
});

PUBLIC_UI.IT = {login:"Accedi",getStarted:"Inizia",dashboard:"Dashboard",contact:"Contatto",messageSent:"Messaggio inviato",thankYou:"Grazie!",contactSuccess:"Grazie! Abbiamo ricevuto il tuo messaggio e risponderemo entro 24 ore.",sendMessage:"Inviaci un messaggio",fullName:"Nome completo",email:"Email",subject:"Oggetto",message:"Messaggio",send:"Invia messaggio",languageLabel:"Lingue",chooseLanguage:"Scegli lingua",helpCenter:"Centro assistenza",coach:"Coach LITRA!",coachUnavailable:"Il Coach LITRA! è disponibile nella dashboard dopo l'accesso.",gotIt:"Ho capito",legal:"Legale",gdpr:"GDPR",lastUpdated:"Ultimo aggiornamento: 17 maggio 2026"};
PUBLIC_UI.NL = {login:"Inloggen",getStarted:"Starten",dashboard:"Dashboard",contact:"Contact",messageSent:"Bericht verzonden",thankYou:"Dank je!",contactSuccess:"Dank je! We hebben je bericht ontvangen en reageren binnen 24 uur.",sendMessage:"Stuur ons een bericht",fullName:"Volledige naam",email:"E-mail",subject:"Onderwerp",message:"Bericht",send:"Bericht verzenden",languageLabel:"Talen",chooseLanguage:"Kies taal",helpCenter:"Helpcentrum",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach is beschikbaar in je dashboard na inloggen.",gotIt:"Begrepen",legal:"Juridisch",gdpr:"AVG",lastUpdated:"Laatst bijgewerkt: 17 mei 2026"};
PUBLIC_UI.SE = {login:"Logga in",getStarted:"Kom igång",dashboard:"Översikt",contact:"Kontakt",messageSent:"Meddelande skickat",thankYou:"Tack!",contactSuccess:"Tack! Vi har tagit emot ditt meddelande och svarar inom 24 timmar.",sendMessage:"Skicka ett meddelande",fullName:"Fullständigt namn",email:"E-post",subject:"Ämne",message:"Meddelande",send:"Skicka meddelande",languageLabel:"Språk",chooseLanguage:"Välj språk",helpCenter:"Hjälpcenter",coach:"LITRA! Coach",coachUnavailable:"LITRA! Coach finns i din översikt efter inloggning.",gotIt:"Förstått",legal:"Juridik",gdpr:"GDPR",lastUpdated:"Senast uppdaterad: 17 maj 2026"};
CONTACT_SUBJECTS.IT = ["Richiesta generale","Supporto fatturazione","Problema tecnico","Partnership","Altro"];
CONTACT_SUBJECTS.NL = ["Algemene vraag","Facturatieondersteuning","Technisch probleem","Partnerschap","Overig"];
CONTACT_SUBJECTS.SE = ["Allmän fråga","Faktureringssupport","Tekniskt problem","Partnerskap","Övrigt"];

Object.keys(LANDING_TRANSLATIONS).forEach(code => {
  LANDING_TRANSLATIONS[code].income = LANDING_INCOME_TRANSLATIONS[code] || LANDING_INCOME_TRANSLATIONS.EN;
});

export { LANDING_TRANSLATIONS, PUBLIC_UI, PUBLIC_LANGS, CONTACT_SUBJECTS, setPreferredLanguage }
